import type { CustomerSupportRequest } from '../../customerSupportApi'
import { formatDateTime } from '../../helpers/formatters'
import { toSupportStatusBadgeClasses, toSupportStatusLabel } from '../../helpers/status'
import { CustomerAreaSurfaceCard } from '../customer-area'

type CustomerSupportHistoryCardProps = {
  title: string
  subtitle: string
  loadingLabel: string
  emptyLabel: string
  openedAtLabel: (value: string) => string
  openTicketDetailLabel: string
  locale: string
  language: 'it' | 'en'
  loading: boolean
  requests: CustomerSupportRequest[]
  previewFallback: string
  neverLabel: string
  onOpenTicket: (requestId: string) => void
}

const toPreview = (value: string, fallback: string) => {
  const trimmed = value.trim()
  if (!trimmed) return fallback
  return trimmed.length > 160 ? `${trimmed.slice(0, 157)}...` : trimmed
}

export const CustomerSupportHistoryCard = ({
  title,
  subtitle,
  loadingLabel,
  emptyLabel,
  openedAtLabel,
  openTicketDetailLabel,
  locale,
  language,
  loading,
  requests,
  previewFallback,
  neverLabel,
  onOpenTicket,
}: CustomerSupportHistoryCardProps) => (
  <CustomerAreaSurfaceCard title={title} subtitle={subtitle}>
    {loading ? (
      <p className="ca-theme-transition rounded-xl border border-(--color-border-default) bg-(--color-bg-card) px-3 py-2 text-sm text-(--color-text-secondary)">
        {loadingLabel}
      </p>
    ) : null}

    {!loading && requests.length === 0 ? (
      <p className="ca-theme-transition rounded-xl border border-(--color-border-default) bg-(--color-bg-card) px-3 py-2 text-sm text-(--color-text-secondary)">
        {emptyLabel}
      </p>
    ) : null}

    {!loading && requests.length > 0 ? (
      <ul className="space-y-2.5">
        {requests.map((request) => (
          <li
            key={request.id}
            className="ca-theme-transition rounded-2xl border border-(--color-border-default) bg-(--color-bg-card) px-4 py-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-(--color-text-primary)">{request.subject}</p>
                <p className="mt-1 text-xs text-(--color-text-muted)">
                  {openedAtLabel(formatDateTime(request.createdAt, locale, neverLabel))}
                </p>
              </div>
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${toSupportStatusBadgeClasses(
                  request.status,
                )}`}
              >
                {toSupportStatusLabel(request.status, language)}
              </span>
            </div>
            <p className="mt-2 text-sm text-(--color-text-secondary)">{toPreview(request.message, previewFallback)}</p>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-(--color-text-muted)">
              <span>ID {request.id}</span>
              <button
                type="button"
                onClick={() => onOpenTicket(request.id)}
                className="ca-btn-secondary inline-flex h-7 items-center rounded-lg px-2.5 py-1 font-semibold"
              >
                {openTicketDetailLabel}
              </button>
            </div>
          </li>
        ))}
      </ul>
    ) : null}
  </CustomerAreaSurfaceCard>
)
